import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  ArrowLeft, 
  Calculator,
  Beaker,
  Info,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalculatorPage = () => {
  const [formData, setFormData] = useState({
    feed_flow: "",
    dosage_rate: "",
    specific_gravity: "1.10",
    pump_capacity_gpd: "",
    product: "s-200"
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Prevent negative values
    let sanitizedValue = value;
    if (parseFloat(value) < 0) {
      sanitizedValue = "0";
    }

    // Apply maximum limits for extreme values
    const maxLimits = {
      feed_flow: 100000,        // Max 100,000 GPM
      dosage_rate: 10000,       // Max 10,000 ppm
      specific_gravity: 5,       // Max 5.0 SG
      pump_capacity_gpd: 10000  // Max 10,000 GPD
    };

    if (maxLimits[name] && parseFloat(sanitizedValue) > maxLimits[name]) {
      sanitizedValue = maxLimits[name].toString();
    }

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    setError(null);
  };

  const handleProductChange = (product) => {
    setFormData(prev => ({ ...prev, product }));
    setResult(null);
    setError(null);
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setError(null);

    // Edge case validation
    if (!formData.pump_capacity_gpd || parseFloat(formData.pump_capacity_gpd) <= 0) {
      setError("Pump capacity required. Please enter a valid pump capacity in GPD.");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        feed_flow: parseFloat(formData.feed_flow),
        dosage_rate: parseFloat(formData.dosage_rate),
        specific_gravity: parseFloat(formData.specific_gravity),
        pump_capacity_gpd: parseFloat(formData.pump_capacity_gpd),
        auto_dilution: true,    // Always auto - 50% pump target
        dilution_factor: null,
        product: formData.product
      };
      const response = await axios.post(`${API}/calculate-dosage-extended`, payload);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Calculation error. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const canCalculate = formData.feed_flow && formData.dosage_rate && formData.specific_gravity &&
    formData.pump_capacity_gpd &&
    parseFloat(formData.feed_flow) > 0 && parseFloat(formData.dosage_rate) > 0 &&
    parseFloat(formData.pump_capacity_gpd) > 0;

  // Determine pump status based on recommended range (30-70%)
  const getPumpStatus = (percent) => {
    if (percent > 100) return { color: "bg-red-500", status: "exceeded", message: "Capacity Exceeded", inRange: false };
    if (percent > 90) return { color: "bg-orange-500", status: "high", message: "Near Maximum", inRange: false };
    if (percent > 70) return { color: "bg-yellow-500", status: "aboverange", message: "Above Recommended", inRange: false };
    if (percent >= 30) return { color: "bg-green-500", status: "optimal", message: "In Recommended Range", inRange: true };
    if (percent >= 10) return { color: "bg-yellow-500", status: "belowrange", message: "Below Recommended", inRange: false };
    return { color: "bg-red-500", status: "critical", message: "Critically Low", inRange: false };
  };

  const feedRateWouldBeZero = !formData.feed_flow || !formData.dosage_rate ||
    parseFloat(formData.feed_flow) <= 0 || parseFloat(formData.dosage_rate) <= 0;

  return (
    <Layout>
      <TooltipProvider>
        <div className="py-8 md:py-12" data-testid="calculator-page">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            {/* Header */}
            <Link 
              to="/"
              className="inline-flex items-center text-[#00BCD4] hover:text-[#00acc1] font-medium mb-6"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </Link>

            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-[#00BCD4]/10 flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-[#00BCD4]" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight mb-3">
                Dosage Calculator
              </h1>
              <p className="text-slate-600 max-w-xl mx-auto">
                Calculate the chemical feed rate for your RO/NF system based on flow rate and desired dosage.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Calculator Form */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
                <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
                  <Beaker className="w-5 h-5 text-[#00BCD4]" />
                  Input Parameters
                </h2>

                <form onSubmit={handleCalculate} className="space-y-6">
                  {/* Flow Rate */}
                  <div>
                    <Label htmlFor="feed_flow" className="text-sm font-medium text-slate-700">
                      Feed Flow (GPM)
                    </Label>
                    <Input
                      type="number"
                      id="feed_flow"
                      name="feed_flow"
                      value={formData.feed_flow}
                      onChange={handleInputChange}
                      placeholder="Enter feed flow in GPM"
                      className="calc-input mt-2"
                      step="0.1"
                      min="0"
                      data-testid="input-feed-flow"
                    />
                  </div>

                  {/* Dosage Rate */}
                  <div>
                    <Label htmlFor="dosage_rate" className="text-sm font-medium text-slate-700">
                      Antiscalant Dosage (ppm)
                    </Label>
                    <Input
                      type="number"
                      id="dosage_rate"
                      name="dosage_rate"
                      value={formData.dosage_rate}
                      onChange={handleInputChange}
                      placeholder="Enter dosage in ppm"
                      className="calc-input mt-2"
                      step="0.1"
                      min="0"
                      data-testid="input-dosage-rate"
                    />
                  </div>

                  {/* Specific Gravity */}
                  <div>
                    <Label htmlFor="specific_gravity" className="text-sm font-medium text-slate-700">
                      Specific Gravity
                    </Label>
                    <Input
                      type="number"
                      id="specific_gravity"
                      name="specific_gravity"
                      value={formData.specific_gravity}
                      onChange={handleInputChange}
                      placeholder="Enter specific gravity"
                      className="calc-input mt-2"
                      step="0.01"
                      min="0.1"
                      data-testid="input-specific-gravity"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Default: 1.10 (typical for antiscalants)
                    </p>
                  </div>

                  {/* Chem Pump Size */}
                  <div>
                    <Label htmlFor="pump_capacity_gpd" className="text-sm font-medium text-slate-700">
                      Chem Pump Size (GPD)
                    </Label>
                    <Input
                      type="number"
                      id="pump_capacity_gpd"
                      name="pump_capacity_gpd"
                      value={formData.pump_capacity_gpd}
                      onChange={handleInputChange}
                      placeholder="Enter pump capacity in GPD"
                      className="calc-input mt-2"
                      step="0.1"
                      min="0"
                      data-testid="input-pump-capacity"
                    />
                    {(!formData.pump_capacity_gpd || parseFloat(formData.pump_capacity_gpd) <= 0) && formData.pump_capacity_gpd !== "" && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Pump capacity required
                      </p>
                    )}
                  </div>

                  {/* Auto Mode Info */}
                  <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3">
                    <p className="text-xs text-cyan-800 flex items-start gap-2">
                      <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Dilution is automatically calculated to maintain ~50% pump operation for optimal performance.
                      </span>
                    </p>
                  </div>

                  {/* Concentrate Option (Informational) */}
                  <label
                    className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                    data-testid="concentrate-checkbox-wrapper"
                  >
                    <input
                      type="checkbox"
                      checked={formData.product === "s-200c"}
                      onChange={(e) => handleProductChange(e.target.checked ? "s-200c" : "s-200")}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#00BCD4] focus:ring-[#00BCD4]"
                      data-testid="concentrate-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Package className="w-4 h-4 text-[#00BCD4]" />
                        Using concentrate product (~10× stronger)
                      </span>
                      <p className="text-xs text-slate-500 mt-1">
                        Concentrate formulations (e.g., S-200C): 5 gallons can treat approximately 11,000,000 gallons of total feed flow.
                      </p>
                    </div>
                  </label>

                  {/* Error Display */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!canCalculate || loading}
                    className="w-full bg-[#00BCD4] hover:bg-[#00acc1] py-3 text-lg"
                    data-testid="calculate-btn"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="spinner w-5 h-5 mr-2"></div>
                        Calculating...
                      </span>
                    ) : (
                      <>
                        <Calculator className="w-5 h-5 mr-2" />
                        Calculate
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Result */}
              <div>
                {result ? (
                  <div className="calc-result animate-fadeIn" data-testid="extended-calc-result">
                    {/* Coverage Message (S-200C only) */}
                    {result.coverage_message && (
                      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-testid="coverage-message">
                        <Package className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-800">{result.coverage_message}</p>
                      </div>
                    )}

                    {/* Warnings */}
                    {result.warnings && result.warnings.length > 0 && (
                      <div className="mb-4 space-y-2">
                        {result.warnings.map((warning, idx) => (
                          <div 
                            key={idx} 
                            className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
                            data-testid="warning-message"
                          >
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-800">{warning}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Success message when in recommended range */}
                    {result.warnings?.length === 0 && getPumpStatus(result.pump_setting_percent).inRange && (
                      <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-green-800">
                          Pump operating within recommended range (30% – 70%)
                        </p>
                      </div>
                    )}

                    {/* Primary Results */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center">
                        <h3 className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1">
                          oz / gallon
                        </h3>
                        <div className="text-2xl font-bold text-[#00BCD4] font-mono">
                          {result.oz_per_gallon}
                        </div>
                        <span className="text-xs text-slate-500">oz per gallon</span>
                      </div>
                      <div className="text-center border-l border-r border-slate-200">
                        <h3 className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1">
                          Pump Setting
                        </h3>
                        <div className={`text-2xl font-bold font-mono ${
                          getPumpStatus(result.pump_setting_percent).inRange ? 'text-green-600' :
                          result.pump_setting_percent > 100 ? 'text-red-500' :
                          result.pump_setting_percent < 10 ? 'text-red-500' :
                          'text-amber-500'
                        }`}>
                          {result.pump_setting_percent}%
                        </div>
                        <span className="text-xs text-slate-500">of capacity</span>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <h3 className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                            Dilution
                          </h3>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-slate-400 hover:text-slate-600">
                                <HelpCircle className="w-3 h-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-sm">
                                Dilution Factor = Total solution ÷ Chemical concentrate
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="text-2xl font-bold text-[#00BCD4] font-mono">
                          {result.dilution_factor}x
                        </div>
                        <span className="text-xs text-slate-500">auto</span>
                      </div>
                    </div>

                    {/* Pump Visual Indicator */}
                    <div className="bg-white rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-xs font-medium text-slate-600">Pump Utilization</span>
                          <p className="text-xs text-slate-400">Recommended Range: 30% – 70%</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{result.pump_setting_percent}%</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            getPumpStatus(result.pump_setting_percent).inRange 
                              ? 'bg-green-100 text-green-700' 
                              : getPumpStatus(result.pump_setting_percent).status === 'exceeded' || 
                                getPumpStatus(result.pump_setting_percent).status === 'critical'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {getPumpStatus(result.pump_setting_percent).message}
                          </span>
                        </div>
                      </div>
                      <div className="h-4 bg-slate-200 rounded-full overflow-hidden relative">
                        <div 
                          className="absolute h-full bg-green-200"
                          style={{ left: '30%', width: '40%' }}
                        />
                        <div 
                          className={`h-full rounded-full transition-all duration-500 relative z-10 ${
                            getPumpStatus(result.pump_setting_percent).color
                          }`}
                          style={{ width: `${Math.min(result.pump_setting_percent, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-slate-400">
                        <span>0%</span>
                        <span className="text-green-600">30%</span>
                        <span>50%</span>
                        <span className="text-green-600">70%</span>
                        <span>100%</span>
                      </div>
                      
                      {result.pump_setting_percent >= 49 && result.pump_setting_percent <= 51 && (
                        <p className="text-xs text-green-600 mt-3 flex items-center gap-1 border-t border-slate-100 pt-2">
                          <CheckCircle2 className="w-3 h-3" />
                          Dilution automatically adjusted to maintain ~50% pump operation
                        </p>
                      )}
                    </div>

                    {/* Additional Details */}
                    <div className="bg-white rounded-lg p-4 text-left">
                      <h4 className="text-xs font-bold tracking-wider text-slate-400 uppercase mb-3">
                        Calculation Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Pump Max:</span>
                          <span className="font-mono font-medium">{result.pump_max_gpm?.toFixed(6)} GPM</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Dilution Factor:</span>
                          <span className="font-mono font-medium">{result.dilution_factor}x</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-100 pt-2">
                          <span className="text-slate-600">oz per gallon:</span>
                          <span className="font-mono font-medium">{result.oz_per_gallon} oz/gal</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-3 mt-3">
                        {result.note || "Calculator logic aligned with product dilution worksheet calculations."}
                      </p>
                    </div>

                    {/* Formulas - Collapsible */}
                    <details className="bg-white rounded-lg p-4 text-left mt-4">
                      <summary className="text-xs font-bold tracking-wider text-slate-400 uppercase cursor-pointer hover:text-slate-600">
                        View Formulas Used
                      </summary>
                      <div className="space-y-1 text-xs font-mono text-slate-600 bg-slate-50 p-3 rounded mt-3 overflow-x-auto">
                        {Object.entries(result.formulas).map(([key, value]) => (
                          <div key={key} className="whitespace-nowrap py-1 border-b border-slate-100 last:border-0">
                            <span className="text-slate-400">{key}:</span> {value}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">
                      {feedRateWouldBeZero ? "Enter Inputs to Calculate" : "Ready to Calculate"}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {feedRateWouldBeZero 
                        ? "Fill in the feed flow, dosage, specific gravity, and pump capacity to calculate."
                        : "Click the calculate button to see results."}
                    </p>
                  </div>
                )}

                {/* Info Card */}
                <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6">
                  <h3 className="text-sm font-bold text-slate-800 mb-3">
                    Understanding the Calculation
                  </h3>
                  <div className="space-y-3 text-sm text-slate-600">
                    <p>
                      <strong>Pump Max (GPM):</strong><br />
                      Chem Pump GPD ÷ 1440
                    </p>
                    <p>
                      <strong>Pump %:</strong><br />
                      (100 × Dosage × 0.000001 × Feed Flow × Dilution Factor) ÷ Pump Max
                    </p>
                    <p>
                      <strong>oz per gallon:</strong><br />
                      128 ÷ Dilution Factor
                    </p>
                    <p>
                      <strong>Auto Dilution Factor:</strong><br />
                      (0.5 × Pump Max) ÷ (Dosage × 0.000001 × Feed Flow)
                    </p>
                    <div className="bg-green-50 border border-green-100 rounded p-3 mt-2">
                      <p className="text-xs text-green-800">
                        <strong>Recommended Pump Range:</strong> 30% – 70% for optimal accuracy and reliability
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded p-3">
                      <p className="text-xs text-blue-800">
                        <strong>Unit Reference:</strong> 128 oz = 1 gallon · 1440 min = 1 day
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 border-t border-slate-100 pt-3 mt-3">
                      Note: Calculator logic aligned with product dilution worksheet calculations. Actual dosage requirements may vary based on water chemistry and operating conditions.
                      Consult with AXEON technical support for specific recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    </Layout>
  );
};

export default CalculatorPage;
